/* eslint-disable react-refresh/only-export-components */
// Provider ve useAuth kasten aynı dosyada tutulur (standart React deseni).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { SESSION_EXPIRED_EVENT, SESSION_RETRY_EVENT, isFatalAuthError, tokenStore } from "../api/client";
import { api } from "../api/endpoints";
import type { User } from "../api/types";
import { useToast } from "../components/ui/Toast";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const navigate = useNavigate();
  const { toast } = useToast();

  // Oturum açıksa kimliği doğrula (sayfa yenilemede).
  //
  // Geçici hata (429/5xx/ağ) oturumu SİLMEZ: aksi halde rate limit'e takılan
  // tek bir /auth/me çağrısı kullanıcıyı login ekranına atıyordu (token'lar da
  // localStorage'dan silindiği için geri dönüş yoktu). 401/403 gibi kesin
  // reddetme ise oturumu kapatır.
  useEffect(() => {
    let cancelled = false;
    const tokens = tokenStore.get();
    if (!tokens) {
      setStatus("anonymous");
      return;
    }

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    void (async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const me = await api.auth.me();
          if (cancelled) return;
          setUser(me);
          setStatus("authenticated");
          return;
        } catch (error) {
          if (cancelled) return;
          if (isFatalAuthError(error)) {
            tokenStore.clear();
            setUser(null);
            setStatus("anonymous");
            return;
          }
          if (attempt < 3) {
            await sleep(400 * 2 ** attempt);
            continue;
          }
          // Kalıcı geçici hata: oturumu kapatma, token'ları koru. Kullanıcı
          // login ekranını görür; sunucu toparlanınca sayfa yenilemesi
          // oturumu geri getirir (davranış: sessiz logout yok).
          setUser(null);
          setStatus("anonymous");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh KESİN reddedilirse oturum sonlandırılır; geçici hatada (429/5xx/ağ)
  // oturum açık kalır ve kullanıcı yalnızca bilgilendirilir.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus("anonymous");
      navigate("/login", { replace: true });
    };
    const onRetry = () =>
      toast(
        "Oturum sunucusuna şimdilik ulaşılamadı; oturumunuz açık, sonraki istekte yeniden deneniyor.",
        "info",
      );
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener(SESSION_RETRY_EVENT, onRetry);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener(SESSION_RETRY_EVENT, onRetry);
    };
  }, [navigate, toast]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.auth.login({ email, password });
    tokenStore.set({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
    });
    setUser(response.user);
    setStatus("authenticated");
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    const tokens = tokenStore.get();
    try {
      // Sözleşme: POST /auth/logout gövdesi RefreshRequest.
      if (tokens) await api.auth.logout(tokens.refreshToken);
    } catch {
      /* oturum zaten geçersiz olabilir */
    }
    tokenStore.clear();
    setUser(null);
    setStatus("anonymous");
    navigate("/login", { replace: true });
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, logout }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth, AuthProvider içinde kullanılmalıdır.");
  return context;
}